import { Form, Table } from 'react-bootstrap';
import { MdAdd, MdDeleteForever } from 'react-icons/md';
import { Contact } from '@blisspointmedia/bpm-types/dist/StreamingNetworks';
import { Checkbox } from '@Components/FormCheck';
import { IconButton } from '@Components/IconButton';
import { Button } from '@Components/Button';
import { Size } from '@Components/designConstants';
import { StreamingNetworkRecord, updateAt } from './streaming-networks-constants';

interface NetworkContactsTabProps {
  network: StreamingNetworkRecord;
  editing: boolean;
  onFieldChange: <K extends keyof StreamingNetworkRecord>(
    field: K,
    value: StreamingNetworkRecord[K]
  ) => void;
}

export const NetworkContactsTab = ({
  network,
  editing,
  onFieldChange
}: NetworkContactsTabProps) => {
  const { contacts, platforms } = network;

  const setContacts = (updated: Contact[]) => onFieldChange('contacts', updated);

  const updateContact = (originalIndex: number, updated: Contact) =>
    setContacts(updateAt(contacts, originalIndex, updated));

  // A real toggle, not a radio -- clicking the current primary contact unsets it, leaving the
  // platform with no primary (the IO/PDF query already handles that case by falling back to any
  // contact on the platform when none is marked primary).
  const setPrimary = (originalIndex: number, platform: string) =>
    setContacts(
      contacts.map((contact, i) => {
        if (i === originalIndex) return { ...contact, isPrimary: !contact.isPrimary };
        if (contact.platform === platform) return { ...contact, isPrimary: false };
        return contact;
      })
    );

  const removeContact = (originalIndex: number) =>
    updateContact(originalIndex, { ...contacts[originalIndex], deleted: true });

  const addContact = (platform: string) =>
    // A new contact has no real id until the backend assigns one on insert — 0 is the closest
    // falsy sentinel our stricter `id: number` type allows.
    setContacts([
      ...contacts,
      {
        id: 0,
        first: '',
        last: '',
        email: '',
        phone: null,
        platform,
        isPrimary: false,
        deleted: false
      }
    ]);

  return (
    <div className='sn-contacts'>
      {platforms
        .filter(platform => !platform.deleted)
        .map(platform => (
          <div className='sn-contacts-platform' key={platform.platform}>
            <div className='sn-contacts-platform-name'>{platform.platform || '(default)'}</div>
            <Table size='sm' className='sn-contacts-table'>
              <thead>
                <tr>
                  <th>First</th>
                  <th>Last</th>
                  <th>Email</th>
                  <th>Primary</th>
                  {editing && <th aria-label='Remove' />}
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact, originalIndex) =>
                  !contact.deleted && contact.platform === platform.platform ? (
                    <ContactRow
                      key={originalIndex}
                      contact={contact}
                      editing={editing}
                      onChange={updated => updateContact(originalIndex, updated)}
                      onSetPrimary={() => setPrimary(originalIndex, platform.platform)}
                      onRemove={() => removeContact(originalIndex)}
                    />
                  ) : undefined
                )}
              </tbody>
            </Table>
            {editing && (
              <Button
                className='sn-add-action'
                leadingElement={<MdAdd />}
                onClick={() => addContact(platform.platform)}
              >
                Add contact
              </Button>
            )}
          </div>
        ))}
    </div>
  );
};

interface ContactRowProps {
  contact: Contact;
  editing: boolean;
  onChange: (updated: Contact) => void;
  onSetPrimary: () => void;
  onRemove: () => void;
}

const ContactRow = ({ contact, editing, onChange, onSetPrimary, onRemove }: ContactRowProps) => (
  <tr>
    <td>
      <Form.Control
        size='sm'
        disabled={!editing}
        value={contact.first}
        onChange={event => onChange({ ...contact, first: event.target.value })}
      />
    </td>
    <td>
      <Form.Control
        size='sm'
        disabled={!editing}
        value={contact.last}
        onChange={event => onChange({ ...contact, last: event.target.value })}
      />
    </td>
    <td>
      <Form.Control
        size='sm'
        disabled={!editing}
        value={contact.email}
        onChange={event =>
          onChange({
            ...contact,
            originalEmail: contact.originalEmail ?? contact.email,
            email: event.target.value
          })
        }
      />
    </td>
    <td>
      <Checkbox checked={contact.isPrimary} disabled={!editing} onChange={onSetPrimary} />
    </td>
    {editing && (
      <td>
        <IconButton size={Size.MD} onClick={onRemove} aria-label='Remove contact'>
          <MdDeleteForever />
        </IconButton>
      </td>
    )}
  </tr>
);
